import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, ChevronsUpDown, Send } from "lucide-react";
import { Badge, Button, Input } from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useBreadcrumbs } from "@/components/layout/BreadcrumbsContext";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { Pagination } from "@/components/layout/Pagination";
import { FilterChipGroup } from "@/components/portfolioAdmin/registry/FilterChipGroup";
import { InternalUserPicker } from "@/components/users/InternalUserPicker";
import { LogInAsAction } from "@/components/impersonation/LogInAsAction";
import {
  portalAccountsApi,
  type PortalAccountDateField,
  type PortalAccountListItemDto,
  type PortalAccountSortBy,
  type PortalAccountSortDir,
  type PortalAccountStatusFilter,
} from "@/api/portalAccounts";
import { type MaterialAudience } from "@/api/portalMaterials";
import {
  fallbackUserLabel,
  useInternalUserLabels,
} from "@/api/internalUsers";
import { portalAccountsKeys } from "@/hooks/portalAccounts/portalAccountsKeys";
import { useDateFormatter } from "@/utils/datetime";
import { cn } from "@/design-system/lib/utils";

// External Intake & Collection — WS4: Portal Accounts list page. Cross-account
// triage for the four typed party portals (Buyer/Seller/Vendor/Broker). All
// filter + sort state is URL-persisted so deep links from the sidebar (or a
// teammate) render the same page with the same filters pre-applied.

const PAGE_SIZE = 25;

// Lead Conversion Router (WS-4) — the portal-account type filter. Deliberately a
// page-local list (NOT the shared MATERIAL_AUDIENCES, which also drives the
// signup-wizard account-type picker + portal-materials publish): Brokerage is a
// real portal account type to FILTER by, but it is provisioned only via the
// staff BrokerageService.InvitePortalAccountAsync path, never the generic
// signup-link flow — so adding it to the shared array would surface a broken
// account-type option in the wizard (the footgun the shared array guards against).
const ACCOUNT_TYPE_FILTER: MaterialAudience[] = [
  "Broker",
  "Buyer",
  "Seller",
  "Vendor",
  "Tps",
  "Brokerage",
];

const STATUS_CHIPS: ReadonlyArray<{
  value: PortalAccountStatusFilter;
  label: string;
}> = [
  { value: "awaiting", label: "Awaiting confirmation" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
  { value: "archived", label: "Archived" },
];

const STATUS_VALUES = new Set<PortalAccountStatusFilter>(
  STATUS_CHIPS.map((s) => s.value),
);

const DATE_FIELDS: ReadonlyArray<{
  value: PortalAccountDateField;
  label: string;
}> = [
  { value: "createdAt", label: "Created" },
  { value: "lastLoginUtc", label: "Last login" },
  { value: "lastActivityAt", label: "Last activity" },
];

const SORT_KEYS: ReadonlyArray<PortalAccountSortBy> = [
  "createdAt",
  "lastLoginUtc",
  "lastActivityAt",
  "overdueCount",
  "awaitingReviewCount",
];

// Account-First — four-way urgency band. "overdue" = at least one overdue
// submission; "awaiting" = awaiting-review with nothing overdue; "clear" =
// active packages but nothing pending; "idle" = no active packages.
type Urgency = "overdue" | "awaiting" | "clear" | "idle";

function urgencyOf(a: PortalAccountListItemDto): Urgency {
  if (a.overdueCount > 0) return "overdue";
  if (a.awaitingReviewCount > 0) return "awaiting";
  if (a.packageCount > 0 || a.lastActivityAt) return "clear";
  return "idle";
}

const ROW_TINT: Record<Urgency, string | undefined> = {
  overdue: "bg-status-error/5",
  awaiting: "bg-status-warning/5",
  clear: undefined,
  idle: undefined,
};

const DOT_COLOR: Record<Urgency, string> = {
  overdue: "bg-status-error",
  awaiting: "bg-status-warning",
  clear: "bg-status-success",
  idle: "bg-gray-mid",
};

type KpiCard = "total" | "awaiting" | "overdue" | "noActivity";

interface StatCardProps {
  label: string;
  value: number;
  active: boolean;
  onClick: () => void;
  testId: string;
}

function StatCard({ label, value, active, onClick, testId }: StatCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={cn(
        "text-left border rounded-md p-space-3 bg-white-pure transition-colors",
        "hover:bg-white-warm focus:outline-none focus-visible:shadow-goldRing",
        active
          ? "border-gold ring-2 ring-gold"
          : "border-border-light",
      )}
    >
      <div className="text-caption uppercase tracking-wide text-gray-mid">
        {label}
      </div>
      <div className="font-display text-h3 text-onyx">{value}</div>
    </button>
  );
}

function parseStatus(raw: string[]): PortalAccountStatusFilter[] {
  const out: PortalAccountStatusFilter[] = [];
  const seen = new Set<string>();
  for (const v of raw) {
    if (STATUS_VALUES.has(v as PortalAccountStatusFilter) && !seen.has(v)) {
      out.push(v as PortalAccountStatusFilter);
      seen.add(v);
    }
  }
  return out;
}

function parseSortBy(raw: string | null): PortalAccountSortBy | undefined {
  return raw && (SORT_KEYS as readonly string[]).includes(raw)
    ? (raw as PortalAccountSortBy)
    : undefined;
}

function parseSortDir(raw: string | null): PortalAccountSortDir | undefined {
  return raw === "asc" || raw === "desc" ? raw : undefined;
}

function parseDateField(
  raw: string | null,
): PortalAccountDateField | undefined {
  if (raw === "createdAt" || raw === "lastLoginUtc" || raw === "lastActivityAt")
    return raw;
  return undefined;
}

function parseNoActivityDays(raw: string | null): number | undefined {
  if (!raw) return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function activeKpi(
  awaitingReviewOnly: boolean,
  overdueOnly: boolean,
  noActivityDays: number | undefined,
): KpiCard {
  if (overdueOnly) return "overdue";
  if (awaitingReviewOnly) return "awaiting";
  if (noActivityDays !== undefined) return "noActivity";
  return "total";
}

const SORT_HEADERS: Record<
  PortalAccountSortBy,
  { label: string; columnKey: string }
> = {
  createdAt: { label: "Created", columnKey: "created" },
  lastLoginUtc: { label: "Last login", columnKey: "lastLogin" },
  lastActivityAt: { label: "Last activity", columnKey: "lastActivity" },
  overdueCount: { label: "Overdue", columnKey: "overdueSort" },
  awaitingReviewCount: { label: "Awaiting", columnKey: "awaitingSort" },
};

interface SortHeaderProps {
  label: string;
  column: PortalAccountSortBy;
  sortBy: PortalAccountSortBy | undefined;
  sortDir: PortalAccountSortDir | undefined;
  onCycle: (column: PortalAccountSortBy) => void;
}

function SortHeader({
  label,
  column,
  sortBy,
  sortDir,
  onCycle,
}: SortHeaderProps) {
  const active = sortBy === column;
  const Icon =
    active && sortDir === "asc"
      ? ChevronUp
      : active && sortDir === "desc"
        ? ChevronDown
        : ChevronsUpDown;
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onCycle(column)}
      data-testid={`portal-accounts-sort-${column}`}
      className={cn(
        "inline-flex items-center gap-1 px-space-2 py-[4px] rounded-sm text-small border transition-colors",
        active
          ? "bg-gold-shadow text-white-pure border-gold-shadow"
          : "bg-white-pure text-onyx border-border-light hover:bg-gold-highlight/10",
      )}
    >
      {label}
      <Icon
        size={12}
        strokeWidth={2}
        aria-hidden
        className={active ? undefined : "opacity-50"}
      />
    </button>
  );
}

export function PortalAccountsPage() {
  useBreadcrumbs([
    { label: "External Collection" },
    { label: "Portal Accounts" },
  ]);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  // URL-derived filter + sort state. Defaults are resolved at parse time so
  // the page never schedules a "set defaults" effect that would double-fetch
  // (per CLAUDE.md root-cause class).
  const accountType =
    (params.get("accountType") as MaterialAudience | null) ?? "";
  const search = params.get("q") ?? "";
  const status = parseStatus(params.getAll("status"));
  const assignedToUserId = params.get("assignedToUserId") ?? "";
  const sortBy = parseSortBy(params.get("sortBy"));
  const sortDir = sortBy
    ? (parseSortDir(params.get("sortDir")) ?? "desc")
    : undefined;
  const dateField = parseDateField(params.get("dateField"));
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const awaitingReviewOnly = params.get("awaitingReviewOnly") === "true";
  const overdueOnly = params.get("overdueOnly") === "true";
  const noActivityDays = parseNoActivityDays(params.get("noActivityDays"));
  const includeArchived =
    status.length === 0 && params.get("archived") === "true";

  const [page, setPage] = useState(1);

  // Debounced search input — the typed text drives `q` in the URL after a
  // 300ms quiet period so each keystroke doesn't refire the list query.
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    // Keep the input in sync if the URL is changed externally (e.g. another
    // KPI click clears `q`).
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    if (searchInput === search) return;
    const handle = setTimeout(() => {
      const merged = new URLSearchParams(params);
      if (searchInput) merged.set("q", searchInput);
      else merged.delete("q");
      setParams(merged, { replace: true });
    }, 300);
    return () => clearTimeout(handle);
    // params + setParams are stable across renders for the same URL; we
    // intentionally only react to the input + the resolved URL value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput, search]);

  // Filter + sort + KPI changes should reset to page 1 so we never land on
  // an out-of-range page after the server's matched scope shrinks.
  const filterSignature = useMemo(
    () =>
      JSON.stringify({
        accountType,
        search,
        status,
        assignedToUserId,
        sortBy,
        sortDir,
        dateField,
        from,
        to,
        awaitingReviewOnly,
        overdueOnly,
        noActivityDays,
        includeArchived,
      }),
    [
      accountType,
      search,
      status,
      assignedToUserId,
      sortBy,
      sortDir,
      dateField,
      from,
      to,
      awaitingReviewOnly,
      overdueOnly,
      noActivityDays,
      includeArchived,
    ],
  );
  useEffect(() => {
    setPage(1);
  }, [filterSignature]);

  // The date inputs emit bare YYYY-MM-DD; widen them to explicit UTC day
  // bounds so the inclusive `>= from` / `<= to` server comparisons cover the
  // whole selected day instead of clipping at midnight (a bare date binds to
  // 00:00, which would exclude same-day records on the `to` edge).
  const fromIso = from
    ? from.includes("T")
      ? from
      : `${from}T00:00:00.000Z`
    : undefined;
  const toIso = to
    ? to.includes("T")
      ? to
      : `${to}T23:59:59.999Z`
    : undefined;

  const listKeyParams = {
    accountType: accountType || undefined,
    search: search || undefined,
    status: status.length > 0 ? status : undefined,
    assignedToUserId: assignedToUserId || undefined,
    sortBy,
    sortDir,
    dateField,
    from: fromIso,
    to: toIso,
    awaitingReviewOnly: awaitingReviewOnly || undefined,
    overdueOnly: overdueOnly || undefined,
    noActivityDays,
    includeArchived: includeArchived || undefined,
    page,
  };

  const list = useQuery({
    queryKey: portalAccountsKeys.list(listKeyParams),
    queryFn: () =>
      portalAccountsApi.list({
        accountType: accountType || undefined,
        search: search || undefined,
        status: status.length > 0 ? status : undefined,
        assignedToUserId: assignedToUserId || undefined,
        sortBy,
        sortDir,
        dateField,
        from: fromIso,
        to: toIso,
        awaitingReviewOnly: awaitingReviewOnly || undefined,
        overdueOnly: overdueOnly || undefined,
        noActivityDays,
        includeArchived: includeArchived || undefined,
        offset: (page - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      }),
  });

  const { date } = useDateFormatter();

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const stats = list.data?.stats ?? {
    total: 0,
    awaitingReview: 0,
    overdue: 0,
    noActivity7DPlus: 0,
  };
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Aggregate all distinct assignee ids on the page so the chip names resolve
  // in a single batched /internal/users/labels call.
  const assigneeIds = useMemo(() => {
    const ids = new Set<string>();
    for (const r of rows) {
      if (Array.isArray(r.assignedToUserIds)) {
        for (const id of r.assignedToUserIds) if (id) ids.add(id);
      }
    }
    return [...ids];
  }, [rows]);
  const userLabels = useInternalUserLabels(assigneeIds);

  // ——— filter mutators ————————————————————————————————————————
  const writeParams = (mutate: (next: URLSearchParams) => void) => {
    const merged = new URLSearchParams(params);
    mutate(merged);
    setParams(merged, { replace: true });
  };

  const setAccountType = (next: string) =>
    writeParams((m) => {
      if (next) m.set("accountType", next);
      else m.delete("accountType");
    });

  const setStatus = (next: string[]) =>
    writeParams((m) => {
      m.delete("status");
      for (const v of next) m.append("status", v);
      // Status="archived" subsumes the legacy archived toggle.
      if (next.length > 0) m.delete("archived");
    });

  const setAssignedToUserId = (next: string) =>
    writeParams((m) => {
      if (next) m.set("assignedToUserId", next);
      else m.delete("assignedToUserId");
    });

  const setDateRange = (
    nextField: PortalAccountDateField | "",
    nextFrom: string,
    nextTo: string,
  ) =>
    writeParams((m) => {
      if (nextField) m.set("dateField", nextField);
      else m.delete("dateField");
      if (nextFrom) m.set("from", nextFrom);
      else m.delete("from");
      if (nextTo) m.set("to", nextTo);
      else m.delete("to");
    });

  const cycleSort = (column: PortalAccountSortBy) => {
    writeParams((m) => {
      const wasActive = sortBy === column;
      const nextDir: PortalAccountSortDir | null =
        !wasActive ? "desc" : sortDir === "desc" ? "asc" : null;
      if (nextDir === null) {
        m.delete("sortBy");
        m.delete("sortDir");
      } else {
        m.set("sortBy", column);
        m.set("sortDir", nextDir);
      }
    });
  };

  const setKpi = (card: KpiCard) => {
    writeParams((m) => {
      // Mutually exclusive quick-filters: clearing the other two is what
      // makes the cards behave like radio buttons.
      m.delete("awaitingReviewOnly");
      m.delete("overdueOnly");
      m.delete("noActivityDays");
      if (card === "awaiting") m.set("awaitingReviewOnly", "true");
      else if (card === "overdue") m.set("overdueOnly", "true");
      else if (card === "noActivity") m.set("noActivityDays", "7");
    });
  };

  const setIncludeArchivedToggle = (on: boolean) =>
    writeParams((m) => {
      if (on) m.set("archived", "true");
      else m.delete("archived");
    });

  const currentKpi = activeKpi(
    awaitingReviewOnly,
    overdueOnly,
    noActivityDays,
  );

  const assigneePickerValue = assignedToUserId ? [assignedToUserId] : [];
  const handleAssigneeChange = (next: string[]) =>
    setAssignedToUserId(next[0] ?? "");

  const renderAssignedNames = (r: PortalAccountListItemDto) => {
    const ids = r.assignedToUserIds ?? [];
    if (!ids.length)
      return <span className="text-caption text-gray-mid italic">—</span>;
    return (
      <div className="flex flex-wrap gap-space-1">
        {ids.map((id) => {
          const user = userLabels.lookup(id);
          const name = user?.displayName ?? fallbackUserLabel(id);
          return (
            <Badge key={id} status="info">
              {name}
            </Badge>
          );
        })}
      </div>
    );
  };

  const columns: Column<PortalAccountListItemDto>[] = useMemo(
    () => [
      {
        key: "actions",
        header: "",
        render: (r) => {
          const needsReview = r.totalPendingCount > 0;
          return (
            <div className="flex items-center gap-space-2">
              {needsReview ? (
                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      `/collection/staging?recipient=${encodeURIComponent(r.email)}`,
                    )
                  }
                  className="rounded-md border border-onyx bg-onyx px-space-3 py-1 text-caption text-white-pure hover:bg-onyx/90"
                >
                  Review
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate(`/admin/portal-accounts/${r.id}`)}
                  className="rounded-md border border-border-light px-space-3 py-1 text-caption text-onyx hover:bg-white-warm"
                >
                  Open
                </button>
              )}
              {/* Self-gating on Users.Impersonate + ADMIN. Opens the account's
                  portal in a NEW TAB and leaves this staff session untouched,
                  so the admin keeps their place in the registry. */}
              <LogInAsAction
                target={{
                  kind: "portal",
                  portalUserId: r.id,
                  label: r.fullName || r.email,
                  accountType: r.accountType,
                }}
              />
            </div>
          );
        },
      },
      {
        key: "name",
        header: "Name / Email",
        render: (r) => {
          const urgency = urgencyOf(r);
          return (
            <div className="flex items-start gap-space-2">
              <span
                className={`mt-1 h-2 w-2 shrink-0 rounded-full ${DOT_COLOR[urgency]}`}
                aria-hidden
              />
              <Link
                to={`/admin/portal-accounts/${r.id}`}
                className="font-semibold text-onyx hover:text-gold-shadow"
              >
                <div>{r.fullName}</div>
                <div className="text-small text-gray-mid">{r.email}</div>
              </Link>
            </div>
          );
        },
      },
      {
        key: "type",
        header: "Type",
        render: (r) => <Badge status="info">{r.accountType}</Badge>,
      },
      {
        key: "status",
        header: "Status",
        render: (r) => (
          <div className="flex flex-wrap gap-space-1">
            {r.isDisabled && <Badge status="error">Disabled</Badge>}
            {r.isArchived && <Badge status="archived">Archived</Badge>}
            {r.emailConfirmed ? (
              <Badge status="finalized">Confirmed</Badge>
            ) : (
              <Badge status="review">Pending</Badge>
            )}
          </div>
        ),
      },
      {
        key: "assignedStaff",
        header: "Assigned staff",
        render: (r) => renderAssignedNames(r),
      },
      {
        key: "submissions",
        header: "Submissions",
        render: (r) => {
          const packageCount = r.packageCount ?? 0;
          const totalItems = r.totalItemCount ?? 0;
          const pendingRecipient = r.pendingRecipientActionCount ?? 0;
          if (packageCount === 0 && totalItems === 0 && !r.lastActivityAt)
            return <span className="text-caption text-gray-mid italic">—</span>;
          return (
            <div className="flex flex-wrap gap-space-1">
              <Badge status="info">
                {packageCount} package{packageCount === 1 ? "" : "s"}
              </Badge>
              <Badge status="archived">
                {totalItems} item{totalItems === 1 ? "" : "s"}
              </Badge>
              {pendingRecipient > 0 && (
                <Badge status="review">
                  {pendingRecipient} pending recipient action
                </Badge>
              )}
              {r.overdueCount > 0 && (
                <Badge status="error">{r.overdueCount} overdue</Badge>
              )}
              {r.awaitingReviewCount > 0 && (
                <Badge status="review">
                  {r.awaitingReviewCount} awaiting review
                </Badge>
              )}
              {pendingRecipient === 0 &&
                r.overdueCount === 0 &&
                r.awaitingReviewCount === 0 && (
                  <Badge status="archived">All complete</Badge>
                )}
            </div>
          );
        },
      },
      {
        key: "attachments",
        header: "Attachments",
        render: (r) => r.attachmentCount,
      },
      {
        key: SORT_HEADERS.lastActivityAt.columnKey,
        header: "Last activity",
        render: (r) => (r.lastActivityAt ? date(r.lastActivityAt) : "—"),
      },
      {
        key: SORT_HEADERS.lastLoginUtc.columnKey,
        header: "Last login",
        render: (r) => (r.lastLoginUtc ? date(r.lastLoginUtc) : "—"),
      },
      {
        key: SORT_HEADERS.createdAt.columnKey,
        header: "Created",
        render: (r) => date(r.createdAt),
      },
    ],
    // assignee lookup function changes when /labels resolves — re-render rows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [date, navigate, userLabels.lookup],
  );

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="External Collection"
        title="Portal Accounts"
        actions={
          <Link to="/admin/portal-accounts/send-signup-link">
            <Button size="sm">
              <Send size={14} strokeWidth={1.5} aria-hidden /> Send signup link
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-2 gap-space-3 sm:grid-cols-4">
        <StatCard
          label="Total accounts"
          value={stats.total}
          active={currentKpi === "total"}
          onClick={() => setKpi("total")}
          testId="portal-accounts-kpi-total"
        />
        <StatCard
          label="Awaiting review"
          value={stats.awaitingReview}
          active={currentKpi === "awaiting"}
          onClick={() => setKpi("awaiting")}
          testId="portal-accounts-kpi-awaiting"
        />
        <StatCard
          label="Overdue"
          value={stats.overdue}
          active={currentKpi === "overdue"}
          onClick={() => setKpi("overdue")}
          testId="portal-accounts-kpi-overdue"
        />
        <StatCard
          label="No activity 7d+"
          value={stats.noActivity7DPlus}
          active={currentKpi === "noActivity"}
          onClick={() => setKpi("noActivity")}
          testId="portal-accounts-kpi-no-activity"
        />
      </div>

      <div className="flex flex-col gap-space-3">
        <div className="flex items-center gap-space-3">
          <Input
            type="search"
            placeholder="Search email or name…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="grow"
          />
        </div>
        <div className="flex flex-wrap items-end gap-space-3">
          <div className="w-44">
            <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
              Account type
            </span>
            <Select
              value={accountType}
              onChange={(e) => setAccountType(e.target.value)}
              aria-label="Account type filter"
            >
              <option value="">All types</option>
              {ACCOUNT_TYPE_FILTER.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </div>
          <FilterChipGroup
            label="Status"
            testId="portal-accounts-status"
            options={STATUS_CHIPS}
            selected={status}
            onChange={setStatus}
          />
          {status.length === 0 && (
            // Legacy archived toggle survives only while the status chips are
            // empty — status="archived" subsumes it once chips are in play.
            <label className="inline-flex items-center gap-space-2 text-small text-gray-mid">
              <input
                type="checkbox"
                checked={includeArchived}
                onChange={(e) => setIncludeArchivedToggle(e.target.checked)}
              />
              Show archived
            </label>
          )}
        </div>
        <div className="flex flex-wrap items-end gap-space-3">
          <div className="w-72">
            <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
              Assigned staff
            </span>
            <InternalUserPicker
              value={assigneePickerValue}
              onChange={handleAssigneeChange}
              placeholder="Filter by assignee…"
              maxSelections={1}
            />
          </div>
          <div className="w-40">
            <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
              Date field
            </span>
            <Select
              value={dateField ?? ""}
              onChange={(e) =>
                setDateRange(
                  e.target.value as PortalAccountDateField | "",
                  from,
                  to,
                )
              }
              aria-label="Date field"
            >
              <option value="">(none)</option>
              {DATE_FIELDS.map((d) => (
                <option key={d.value} value={d.value}>
                  {d.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-40">
            <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
              From
            </span>
            <Input
              type="date"
              value={from}
              onChange={(e) =>
                setDateRange(dateField ?? "", e.target.value, to)
              }
              aria-label="From date"
              disabled={!dateField}
            />
          </div>
          <div className="w-40">
            <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
              To
            </span>
            <Input
              type="date"
              value={to}
              onChange={(e) =>
                setDateRange(dateField ?? "", from, e.target.value)
              }
              aria-label="To date"
              disabled={!dateField}
            />
          </div>
        </div>
        <div className="flex flex-wrap gap-space-4">
          <div className="flex flex-col gap-space-1">
            <span className="text-[10px] uppercase tracking-[0.16em] text-gray-mid font-display font-bold">
              Sort
            </span>
            <div className="flex flex-wrap gap-space-1">
              {(
                [
                  "createdAt",
                  "lastLoginUtc",
                  "lastActivityAt",
                  "overdueCount",
                  "awaitingReviewCount",
                ] as PortalAccountSortBy[]
              ).map((col) => (
                <SortHeader
                  key={col}
                  label={SORT_HEADERS[col].label}
                  column={col}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onCycle={cycleSort}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={list.isLoading}
        error={list.isError ? "Failed to load portal accounts." : null}
        emptyMessage={
          accountType
            ? `No ${accountType} accounts yet.`
            : "No portal accounts on file."
        }
        rowKey={(r) => r.id}
        rowClassName={(r) => ROW_TINT[urgencyOf(r)]}
      />

      {total > 0 && (
        <Pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalCount={total}
          totalPages={totalPages}
          onPrev={() => setPage((p) => Math.max(1, p - 1))}
          onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
        />
      )}
    </div>
  );
}
